import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Home,
  Info,
  Briefcase,
  HelpCircle,
  Users2,
  UsersRound,
  HeartHandshake,
  FileQuestion,
  BookOpen,
  UserPlus,
  Send,
  Globe,
  Menu,
  X,
  FileText,
  Package,
  DollarSign,
} from "lucide-react";
import PropTypes from "prop-types";
import "./Navbar.css";

const Navbar = () => { // Removed isTopBarHidden and isMobile props
  const [isAtTop, setIsAtTop] = useState(true);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [particles, setParticles] = useState([]);
  const [expandedMenu, setExpandedMenu] = useState(null);
  const location = useLocation();
  const mobileMenuRef = useRef(null);
  const firstFocusableRef = useRef(null);
  const lastFocusableRef = useRef(null);
  const animationFrameRef = useRef(null);

  const springConfig = { type: "spring", stiffness: 250, damping: 20 };

  const navItems = [
    { name: "Home", path: "/", icon: <Home className="h-4 w-4" /> },
    { name: "About", path: "/about", icon: <Info className="h-4 w-4" /> },
    {
      name: "Products",
      icon: <Package className="h-4 w-4" />,
      children: [
        {
          name: "Vuxo Voice",
          path: "/vuxo-voice",
          icon: (
            <motion.img
              src="https://i.imgur.com/MtQvDiz.png"
              alt="Vuxo Voice Icon"
              className="h-6 md:h-8 w-6 md:w-8"
              whileHover={{ scale: 1.2, rotate: 15, y: -5 }}
              transition={springConfig}
              onError={(e) => (e.target.src = "https://i.imgur.com/MtQvDiz.png")}
            />
          ),
          isMobileTopLevel: true,
        },
        {
          name: "Vuxo Chat",
          path: "/vuxo-chat",
          icon: (
            <motion.img
              src="https://i.imgur.com/2S3iQ40.png"
              alt="Vuxo Chat Icon"
              className="h-6 md:h-8 w-6 md:w-8"
              whileHover={{ scale: 1.2, rotate: 15, y: -5 }}
              transition={springConfig}
              onError={(e) => (e.target.src = "https://i.imgur.com/2S3iQ40.png")}
            />
          ),
          isMobileTopLevel: true,
        },
      ],
    },
    {
      name: "Services",
      path: "/ai-consultancy",
      icon: <Briefcase className="h-4 w-4" />,
      children: [
        {
          name: "Workshops",
          path: "/workshops",
          icon: (
            <motion.img
              src="https://i.imgur.com/SyxPmgj.png"
              alt="Workshops"
              className="h-6 md:h-8 w-6 md:w-8"
              whileHover={{ scale: 1.2, rotate: 15, y: -5 }}
              transition={springConfig}
              onError={(e) => (e.target.src = "https://i.imgur.com/SyxPmgj.png")}
            />
          ),
        },
        {
          name: "App Development",
          path: "/app-development",
          icon: (
            <motion.img
              src="https://i.imgur.com/AX7PQgq.png"
              alt="App Dev Icon"
              className="h-6 md:h-8 w-6 md:w-8"
              whileHover={{ scale: 1.2, rotate: 15, y: -5 }}
              transition={springConfig}
              onError={(e) => (e.target.src = "https://i.imgur.com/AX7PQgq.png")}
            />
          ),
        },
        {
          name: "Website Development",
          path: "/website-development",
          icon: (
            <motion.img
              src="https://i.imgur.com/IcuFHgJ.png"
              alt="Web Dev Icon"
              className="h-6 md:h-8 w-6 md:w-8"
              whileHover={{ scale: 1.2, rotate: 15, y: -5 }}
              transition={springConfig}
              onError={(e) => (e.target.src = "https://i.imgur.com/IcuFHgJ.png")}
            />
          ),
        },
      ],
    },
    { name: "Pricing", path: "/plans", icon: <DollarSign className="h-4 w-4" /> },
    {
      name: "Community",
      icon: <UsersRound className="h-4 w-4" />,
      children: [
        { name: "Partners", path: "/partners", icon: <HeartHandshake className="h-4 w-4" /> },
        { name: "Careers", path: "/careers", icon: <Briefcase className="h-4 w-4" /> },
        { name: "Community Service", path: "/community-service", icon: <Users2 className="h-4 w-4" /> },
        { name: "Become a Partner", path: "/become-a-partner", icon: <UserPlus className="h-4 w-4" /> },
        { name: "Volunteer Hours", path: "/volunteer-hours", icon: <Users2 className="h-4 w-4" /> },
      ],
    },
    {
      name: "Resources",
      icon: <FileQuestion className="h-4 w-4" />,
      children: [
        { name: "Blogs", path: "/blogs", icon: <BookOpen className="h-4 w-4" /> },
        { name: "FAQs", path: "/faqs", icon: <HelpCircle className="h-4 w-4" /> },
        { name: "Content", path: "/content", icon: <FileText className="h-4 w-4" /> },
        { name: "Site Map", path: "/sitemaps", icon: <Globe className="h-4 w-4" /> },
      ],
    },
    { name: "Contact", path: "/contact", icon: <Send className="h-4 w-4" />, isContact: true },
  ];

  const handleScroll = useCallback(() => {
    setIsAtTop(window.scrollY < 10);
  }, []);

  const handleResize = useCallback(() => {
    if (window.innerWidth >= 1024) {
      setMobileMenuOpen(false);
      setExpandedMenu(null);
    }
    handleScroll();
  }, [handleScroll]);

  useEffect(() => {
    const throttledScroll = (func, limit) => {
      let inThrottle;
      return (...args) => {
        if (!inThrottle) {
          func(...args);
          inThrottle = true;
          setTimeout(() => (inThrottle = false), limit);
        }
      };
    };

    const scrollHandler = throttledScroll(handleScroll, 100);
    window.addEventListener("scroll", scrollHandler, { passive: true });
    window.addEventListener("resize", handleResize, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener("scroll", scrollHandler);
      window.removeEventListener("resize", handleResize);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [handleScroll, handleResize]);

  useEffect(() => {
    setMobileMenuOpen(false);
    setParticles([]);
    setExpandedMenu(null);
    setDropdownOpen(null);
  }, [location.pathname]);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.classList.add("mobile-menu-open");
      firstFocusableRef.current?.focus();
    } else {
      document.body.classList.remove("mobile-menu-open");
    }
    return () => document.body.classList.remove("mobile-menu-open");
  }, [mobileMenuOpen]);

  const handleDropdownToggle = useCallback((itemName) => {
    setDropdownOpen((prev) => (prev === itemName ? null : itemName));
  }, []);

  const handleMenuItemClick = useCallback(() => {
    setDropdownOpen(null);
    setMobileMenuOpen(false);
    setParticles([]);
    setExpandedMenu(null);
  }, []);

  const handleKeyDown = useCallback(
    (e, itemName) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handleDropdownToggle(itemName);
      } else if (e.key === "Escape") {
        setDropdownOpen(null);
      } else if (e.key === "ArrowDown" && dropdownOpen === itemName) {
        e.preventDefault();
        const firstSubItem = e.target.nextElementSibling?.querySelector("a");
        firstSubItem?.focus();
      }
    },
    [dropdownOpen, handleDropdownToggle]
  );

  const handleMobileKeyDown = useCallback(
    (e, itemName) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        setExpandedMenu((prev) => (prev === itemName ? null : itemName));
      } else if (e.key === "Escape") {
        setExpandedMenu(null);
        setMobileMenuOpen(false);
      }
    },
    []
  );

  const handleFocusTrap = useCallback((e) => {
    if (!mobileMenuRef.current) return;
    const focusableElements = mobileMenuRef.current.querySelectorAll(
      'a, button, [tabindex="0"]'
    );
    const first = focusableElements[0];
    const last = focusableElements[focusableElements.length - 1];

    firstFocusableRef.current = first;
    lastFocusableRef.current = last;

    if (e.key === "Tab") {
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }, []);

  const isChildActive = useCallback(
    (children) =>
      children?.some((child) => location.pathname.startsWith(child.path)),
    [location.pathname]
  );

  const createParticle = useCallback((x, y) => {
    const particle = {
      id: Math.random(),
      x,
      y,
      vx: (Math.random() - 0.5) * 2,
      vy: (Math.random() - 0.5) * 2,
      life: 0.5,
    };
    setParticles((prev) => [...prev, particle].slice(-5));
  }, []);

  const updateParticles = useCallback(() => {
    setParticles((prev) =>
      prev
        .map((p) => ({
          ...p,
          x: p.x + p.vx,
          y: p.y + p.vy,
          life: p.life - 0.1,
        }))
        .filter((p) => p.life > 0)
    );
    if (mobileMenuOpen) {
      animationFrameRef.current = requestAnimationFrame(updateParticles);
    }
  }, [mobileMenuOpen]);

  useEffect(() => {
    if (mobileMenuOpen) {
      animationFrameRef.current = requestAnimationFrame(updateParticles);
    }
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [mobileMenuOpen, updateParticles]);

  return (
    <motion.nav
      initial={{ y: 0 }}
      animate={{ y: 0 }}
      transition={springConfig}
      className={`navbar ${mobileMenuOpen ? "mobile-menu-open" : ""}`}
      role="navigation"
      aria-label="Main navigation"
    >
      {isAtTop && (
        <motion.div
          className="navbar-bg"
          initial={{ opacity: 1 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={springConfig}
        />
      )}
      <div className="navbar-container">
        <Link
          to="/"
          className="navbar-logo group"
          aria-label="AI Empower Home"
        >
          <motion.div
            whileHover={{ scale: 1.1, rotateY: 15, rotateX: 15 }}
            whileTap={{ scale: 0.95 }}
            transition={springConfig}
            className="navbar-logo-container"
          >
            <img
              src="https://i.imgur.com/0bKyom7.png"
              alt="AI Empower Logo"
              className="navbar-logo-img"
              onError={(e) => (e.target.src = "https://i.imgur.com/0bKyom7.png")}
            />
          </motion.div>
          <motion.span
            animate={{
              textShadow: [
                "0 0 10px rgba(147,51,234,0.3)",
                "0 0 20px rgba(147,51,234,0.5)",
              ],
            }}
            transition={{ duration: 1.2, repeat: Infinity, repeatType: "reverse" }}
          >
            AI Empower
          </motion.span>
        </Link>
        <div className="mobile-menu-button">
          <motion.button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            transition={springConfig}
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-menu"
          >
            {mobileMenuOpen ? (
              <X className="h-6 w-6 text-white" />
            ) : (
              <Menu className="h-6 w-6 text-white" />
            )}
          </motion.button>
        </div>
        <ul className="nav-items">
          {navItems.map((item) =>
            item.children ? (
              <li
                key={item.name}
                className="dropdown-container"
                onMouseEnter={() => handleDropdownToggle(item.name)}
                onMouseLeave={() => setDropdownOpen(null)}
              >
                <motion.button
                  onClick={() => handleDropdownToggle(item.name)}
                  onKeyDown={(e) => handleKeyDown(e, item.name)}
                  className={`nav-item ${
                    dropdownOpen === item.name || isChildActive(item.children)
                      ? "active"
                      : ""
                  }`}
                  whileHover={{ y: -4, rotateX: 10, rotateY: 10 }}
                  whileTap={{ scale: 0.95 }}
                  transition={springConfig}
                  aria-expanded={dropdownOpen === item.name}
                  aria-controls={`dropdown-${item.name}`}
                  aria-label={`${item.name} dropdown`}
                  tabIndex={0}
                >
                  {item.icon}
                  {item.name}
                </motion.button>
                <AnimatePresence>
                  {dropdownOpen === item.name && (
                    <motion.ul
                      id={`dropdown-${item.name}`}
                      initial={{ opacity: 0, y: -10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.95 }}
                      transition={{ duration: 0.2, ease: "easeOut" }}
                      className={`dropdown-menu ${
                        item.name === "Products"
                          ? "products-dropdown"
                          : item.name === "Services"
                          ? "services-dropdown"
                          : ""
                      } ${item.name === "Resources" ? "right-align" : ""}`}
                      role="menu"
                    >
                      {item.children.map((child) => (
                        <li key={child.path} role="menuitem">
                          <Link
                            to={child.path}
                            className={`dropdown-item ${
                              location.pathname === child.path
                                ? "text-indigo-400 font-semibold"
                                : ""
                            }`}
                            onClick={handleMenuItemClick}
                            tabIndex={0}
                            onKeyDown={(e) => {
                              if (e.key === "ArrowDown") {
                                e.preventDefault();
                                const nextItem = e.target.parentElement.nextElementSibling?.querySelector("a");
                                nextItem?.focus();
                              } else if (e.key === "ArrowUp") {
                                e.preventDefault();
                                const prevItem = e.target.parentElement.previousElementSibling?.querySelector("a") || e.target.parentElement.parentElement.previousElementSibling;
                                prevItem?.focus();
                              } else if (e.key === "Escape") {
                                setDropdownOpen(null);
                                e.target.parentElement.parentElement.previousElementSibling.focus();
                              }
                            }}
                          >
                            <motion.div
                              whileHover={{ scale: 1.05, rotate: 3, skewX: 5 }}
                              whileTap={{ scale: 0.95 }}
                              transition={springConfig}
                              className="flex items-center gap-2"
                            >
                              {child.icon}
                              <span>{child.name}</span>
                            </motion.div>
                          </Link>
                        </li>
                      ))}
                    </motion.ul>
                  )}
                </AnimatePresence>
              </li>
            ) : (
              <li key={item.name}>
                <Link to={item.path} onClick={handleMenuItemClick}>
                  <motion.div
                    className={
                      item.isContact
                        ? `contact-item ${
                            location.pathname === item.path ? "active" : ""
                          }`
                        : `nav-item ${
                            location.pathname === item.path ? "active" : ""
                          }`
                    }
                    whileHover={item.isContact ? { y: -2 } : { y: -4, rotateX: 10, rotateY: 10 }}
                    whileTap={{ scale: 0.95 }}
                    transition={springConfig}
                    aria-label={item.name}
                    role="menuitem"
                  >
                    {item.icon}
                    {item.name}
                  </motion.div>
                </Link>
              </li>
            )
          )}
        </ul>
      </div>
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ x: "100%", opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: "100%", opacity: 0 }}
            transition={{ type: "spring", stiffness: 400, damping: 30, mass: 0.8 }}
            className="mobile-menu"
            id="mobile-menu"
            ref={mobileMenuRef}
            onKeyDown={handleFocusTrap}
            onTouchMove={(e) => {
              if (e.touches.length > 0) {
                const touch = e.touches[0];
                const rect = e.currentTarget.getBoundingClientRect();
                createParticle(touch.clientX - rect.left, touch.clientY - rect.top);
              }
            }}
            role="dialog"
            aria-modal="true"
          >
            <div className="mobile-menu-header">
              <Link
                to="/"
                className="navbar-logo group"
                onClick={handleMenuItemClick}
                aria-label="AI Empower Home"
              >
                <motion.div
                  whileHover={{ scale: 1.1, rotateY: 15, rotateX: 15 }}
                  whileTap={{ scale: 0.95 }}
                  transition={springConfig}
                  className="navbar-logo-container"
                >
                  <img
                    src="https://i.imgur.com/0bKyom7.png"
                    alt="AI Empower Logo"
                    className="navbar-logo-img"
                    onError={(e) => (e.target.src = "https://i.imgur.com/0bKyom7.png")}
                  />
                </motion.div>
                <motion.span
                  animate={{
                    textShadow: [
                      "0 0 10px rgba(147,51,234,0.3)",
                      "0 0 20px rgba(147,51,234,0.5)",
                    ],
                  }}
                  transition={{ duration: 1.2, repeat: Infinity, repeatType: "reverse" }}
                >
                  AI Empower
                </motion.span>
              </Link>
              <motion.button
                onClick={() => setMobileMenuOpen(false)}
                whileTap={{ scale: 0.9 }}
                whileHover={{ scale: 1.1 }}
                transition={springConfig}
                className="mobile-menu-close"
                aria-label="Close menu"
              >
                <X className="h-6 w-6 text-white" />
              </motion.button>
            </div>
            <ul className="mobile-nav-items">
              {navItems
                .filter((item) => item.name !== "Products")
                .reduce((acc, item, index) => {
                  if (item.name === "About") {
                    const productsChildren = navItems
                      .find((i) => i.name === "Products")
                      .children.filter((child) => child.isMobileTopLevel);
                    return [
                      ...acc,
                      item,
                      ...productsChildren.map((child) => ({
                        ...child,
                        icon: (
                          <img
                            src={child.icon.props.src}
                            alt={child.icon.props.alt}
                            className="h-6 w-6"
                            onError={child.icon.props.onError}
                          />
                        ),
                      })),
                    ];
                  }
                  return [...acc, item];
                }, [])
                .map((item, index) => {
                  const isExpandable = ["Services", "Community", "Resources"].includes(item.name);
                  return (
                    <li key={item.name}>
                      {item.children && isExpandable ? (
                        <>
                          <motion.div
                            className={`mobile-nav-item ${
                              location.pathname === item.path || isChildActive(item.children)
                                ? "active"
                                : ""
                            }`}
                            initial={{ opacity: 0, x: 50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.08, type: "spring", stiffness: 250 }}
                            onClick={() => setExpandedMenu(expandedMenu === item.name ? null : item.name)}
                            onKeyDown={(e) => handleMobileKeyDown(e, item.name)}
                            role="button"
                            tabIndex={0}
                            aria-expanded={expandedMenu === item.name}
                            aria-controls={`mobile-submenu-${item.name}`}
                          >
                            <item.icon.type className="h-5 w-5" />
                            {item.name}
                            <motion.svg
                              className="h-4 w-4 ml-auto"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              animate={{ rotate: expandedMenu === item.name ? 180 : 0 }}
                              transition={{ duration: 0.3 }}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 9l-7 7-7-7"
                              />
                            </motion.svg>
                          </motion.div>
                          <AnimatePresence>
                            {expandedMenu === item.name && (
                              <motion.ul
                                className="mobile-sub-items"
                                id={`mobile-submenu-${item.name}`}
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.3, ease: "easeOut" }}
                              >
                                {item.children.map((child, childIndex) => (
                                  <li key={child.path}>
                                    <Link
                                      to={child.path}
                                      className={`mobile-sub-item ${
                                        item.name === "Services" ? "services-mobile-sub-item" : ""
                                      } ${
                                        item.name === "Community" ? "community-mobile-sub-item" : ""
                                      } ${location.pathname === child.path ? "active" : ""}`}
                                      onClick={handleMenuItemClick}
                                      tabIndex={0}
                                      onKeyDown={(e) => {
                                        if (e.key === "ArrowDown") {
                                          e.preventDefault();
                                          const nextItem = e.target.parentElement.nextElementSibling?.querySelector("a");
                                          nextItem?.focus();
                                        } else if (e.key === "ArrowUp") {
                                          e.preventDefault();
                                          const prevItem = e.target.parentElement.previousElementSibling?.querySelector("a") || e.target.parentElement.parentElement.previousElementSibling;
                                          prevItem?.focus();
                                        } else if (e.key === "Escape") {
                                          setExpandedMenu(null);
                                          e.target.parentElement.parentElement.previousElementSibling.focus();
                                        }
                                      }}
                                    >
                                      <motion.div
                                        initial={{ opacity: 0, x: 30 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{
                                          delay: (index + childIndex) * 0.04 + 0.1,
                                          type: "spring",
                                        }}
                                        className="flex items-center gap-2"
                                      >
                                        {child.icon}
                                        <span>{child.name}</span>
                                      </motion.div>
                                    </Link>
                                  </li>
                                ))}
                              </motion.ul>
                            )}
                          </AnimatePresence>
                        </>
                      ) : (
                        <Link to={item.path} onClick={handleMenuItemClick}>
                          <motion.div
                            className={
                              item.isContact
                                ? `mobile-contact-item ${
                                    location.pathname === item.path ? "active" : ""
                                  }`
                                : `mobile-nav-item ${
                                    location.pathname === item.path ? "active" : ""
                                  }`
                            }
                            initial={{ opacity: 0, x: 50 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.08, type: "spring", stiffness: 250 }}
                            role="menuitem"
                          >
                            {item.icon}
                            {item.name}
                          </motion.div>
                        </Link>
                      )}
                    </li>
                  );
                })}
            </ul>
            {particles.map((particle) => (
              <motion.div
                key={particle.id}
                className="particle"
                initial={{ x: particle.x, y: particle.y, opacity: particle.life, scale: 1 }}
                animate={{
                  x: particle.x + particle.vx * 5,
                  y: particle.y + particle.vy * 5,
                  opacity: 0,
                  scale: particle.life,
                }}
                transition={{ duration: 0.3, ease: "easeOut" }}
              />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

Navbar.propTypes = {
  isTopBarHidden: PropTypes.bool,
  isMobile: PropTypes.bool,
};

Navbar.defaultProps = {
  isTopBarHidden: false,
  isMobile: false,
};

export default Navbar;